import React, { useContext, useState } from "react";
import { Button, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DocumentItemsFilter } from "src/pages/App/pages/Organization/pages/Document/components/DocumentItemsFilter";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as InfoCircleSVG } from "go-component/images/svg/info-circle.svg";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as AlertSVG } from "go-core/images/svg/alert.svg";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import LinkEntityModal from "../../../../../../../../../../../components/AppLink/LinkEntityModal/LinkEntityModal";
import { ReactComponent as MoreSVG } from "../../../../../../../../../../../images/svg/documents/more.svg";
import {
	LinkEntityApi,
	LinkedApi,
	OrderApi,
	OrderItemApi,
	ProductApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../services/Api/api";
import { getProductVolumeLabel, isItemRowIndexShown, showMore } from "../../../../../utils/itemsTableUtils";
import { OrderContext } from "../../../pages/Preview";
import { getOrderItemNetPriceAmount } from "../../../utils";
import OrderItemsCardTableSummary from "./OrderItemsCardTableSummary";

interface Props {
	fileImportPreviewMode?: boolean;
	importedOrder?: OrderApi;
}
const OrderItemsCardTableBody = ({ fileImportPreviewMode, importedOrder }: Props) => {
	const { t } = useTranslation();
	const { setOrder, order, fetchOrder } = useContext(OrderContext);
	const finalOrder = importedOrder ?? order;
	const [filteredOrderItems, setFilteredOrderItems] = useState<OrderItemApi[]>(finalOrder.items);
	const organization = useSelector(selectOrganization);
	const taxItems = finalOrder?.tax_items || [];
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const [productToLink, setProductToLink] = useState<LinkEntityApi | undefined>(undefined);
	const [productIndexToLink, setProductIndexToLink] = useState<number>(-1);
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();

	const drawCost = (cost?: MoneyApi) => {
		let result = "-";
		if (!isNaN(Number(cost?.amount))) result = FormatMoney(cost);
		return result;
	};

	const drawSaleNetPrice = (item: OrderItemApi) => {
		const amount = getOrderItemNetPriceAmount(item);
		if (amount === null) return "-";

		return FormatMoney({
			amount,
			currency: finalOrder?.total_mac_cost_net_money?.currency || organization?.currency || "",
		});
	};

	const handleRefreshPage = () => {
		const unlinkedItems = finalOrder.items.filter((item) => !item.product);
		if (unlinkedItems.length === 1) {
			window.location.reload();
		}
	};

	const updateLinkedProductWithLink = (linkedProduct: LinkedApi, volume?: number) => {
		const items = [...finalOrder.items];
		const itemToUpdate = { ...items[productIndexToLink] };
		if (!linkedProduct.skipped) {
			let newItem = {
				...itemToUpdate,
				volume: volume ?? itemToUpdate.volume,
			};
			if (linkedProduct.resource) {
				newItem = {
					...newItem,
					product: {
						...itemToUpdate.product,
						id: linkedProduct.resource?.id,
						name: linkedProduct.resource?.name,
						type: linkedProduct.resource?.product_type,
					} as ProductApi,
				};
			}
			items[productIndexToLink] = { ...newItem };
			setOrder({
				...finalOrder,
				items,
			});
		} else {
			const unlinkedProductBox = document.getElementById(`unlinked-product-${itemToUpdate.id}`);
			if (unlinkedProductBox) {
				unlinkedProductBox.className = "d-none";
			}
		}
		handleRefreshPage();
	};

	const updateLinkedProductWithoutLink = (product: ProductApi) => {
		const items = [...finalOrder.items];
		const itemToUpdate = { ...items[productIndexToLink] };
		if (product) {
			items[productIndexToLink] = { ...itemToUpdate, product };
		} else {
			items.splice(productIndexToLink, 1);
		}
		setOrder({
			...finalOrder,
			items,
		});
		handleRefreshPage();
	};

	const onHide = () => {
		setProductToLink(undefined);
		setProductIndexToLink(-1);
	};

	const onLink = (item: OrderItemApi, index: number) => {
		let product = {
			...item?.linked_product,
			tax: item?.tax,
			resource_type: "PRODUCT",
		};
		if (!item?.linked_product?.external_name) {
			product = {
				...product,
				external_name: item?.external_product_name,
			};
		}
		setProductToLink(product as LinkedApi);
		setProductIndexToLink(index);
	};

	const handleCorrection = async (item: OrderItemApi, isProduct: boolean) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			if (isProduct && item?.product_id) {
				await api.organization().correctProductRecipe(item.product_id);
			} else if (item.linked_product?.id) {
				await api.organization().correctIntegrationLinkRecipe(item.linked_product.id);
			}

			fetchOrder();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<tbody>
			<DocumentItemsFilter
				numberOfColumns={11}
				items={order.items}
				setItems={setFilteredOrderItems}
				numberOfFilteredItems={filteredOrderItems.length}
			/>
			{filteredOrderItems.map((item, index) => {
				const indexInOriginalItemsArray = finalOrder.items.findIndex(
					(originalItem) => originalItem.id === item.id
				);
				const measureType = item?.product?.measure_type;

				return (
					<React.Fragment key={finalOrder.id}>
						<tr>
							<td className="action">
								{item?.items?.length > 0 && (
									<DropdownSVG
										className="icon"
										id={`icon-${index}`}
										style={{
											transform: isItemRowIndexShown(shownIndexes, index)
												? "rotate(180deg)"
												: "unset",
										}}
										onClick={() => showMore(shownIndexes, setShownIndexes, index)}
									/>
								)}
							</td>
							<td>
								{item?.product ? (
									<div className="d-flex flex-column">
										<div className="d-flex align-items-center justify-content-between">
											<div className="d-flex column-gap-2 align-items-center">
												<span>{`${indexInOriginalItemsArray + 1}.`}</span>
												<Link
													rel={"noreferrer"}
													target={"_blank"}
													to={`/${organization.id}/products/${item.product.id}/edit`}
													className="product-name"
												>
													{item.product.name}
													{getProductVolumeLabel(item.product, item)}
												</Link>
											</div>
											{item?.external_product_name && (
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip>{item.external_product_name}</Tooltip>}
												>
													<InfoCircleSVG
														className="ms-1"
														onClick={(event) => event.stopPropagation()}
													/>
												</OverlayTrigger>
											)}
										</div>
										{item?.comment && <small className="text-dark">{item.comment}</small>}
									</div>
								) : (
									<div className="d-flex flex-column">
										<span>
											{t("modules.order.field.external_name.title")}:{" "}
											{item?.external_product_name}
										</span>
										<div id={`unlinked-product-${item.id}`}>
											<span className="d-inline-block mt-n1">
												<AlertSVG className="me-1" />
												<small className="text-danger">
													{t("modules.order.field.unconnected_product.title")}
												</small>
											</span>
											{!fileImportPreviewMode && (
												<div className="mt-1">
													<Button onClick={() => onLink(item, index)}>
														{t("common.action.connect")}
													</Button>
												</div>
											)}
										</div>
									</div>
								)}
							</td>
							<td>
								{item.volume} {measureType ? t(`enums.products.measure_types.${measureType}`) : ""}
							</td>
							<td>{item?.quantity}</td>
							<td>
								{(item.volume * item.quantity).toFixed(5)}{" "}
								{measureType ? t(`enums.products.measure_types.${measureType}`) : ""}
							</td>
							<td>{FormatMoney(item.unit_fifo_cost_net_money)}</td>
							<td>{FormatMoney(item.total_fifo_cost_net_money)}</td>
							<td>{drawSaleNetPrice(item)}</td>
							<td>{item?.tax?.name || "-"}</td>
							<td>
								{FormatMoney(
									finalOrder.tax_gross ? item.total_price_gross_money : item.total_price_net_money
								)}
							</td>
							{(item?.product_id || item?.linked_product?.id) && (
								<td className="action">
									<Dropdown className="icon">
										<Dropdown.Toggle as={MoreSVG} />
										<Dropdown.Menu>
											{item?.product_id && (
												<Dropdown.Item onClick={() => handleCorrection(item, true)}>
													{t("common.action.recipe_correction")}
												</Dropdown.Item>
											)}
											{item?.linked_product?.id && (
												<Dropdown.Item onClick={() => handleCorrection(item, false)}>
													{t("common.action.integration_link_correction")}
												</Dropdown.Item>
											)}
										</Dropdown.Menu>
									</Dropdown>
								</td>
							)}
						</tr>
						{isItemRowIndexShown(shownIndexes, index) &&
							item.items.map((subItem, i) => (
								<tr key={`transfer-item-${index}-item-${i}`}>
									<td />
									<td>
										<Link
											rel={"noreferrer"}
											target={"_blank"}
											to={`/${organization.id}/products/${subItem.product.id}/edit`}
											className="ms-4"
										>
											{subItem.product.name}
										</Link>
									</td>
									<td />
									<td>{subItem.quantity.toFixed(5)}</td>
									<td />
									<td>{drawCost(subItem?.unit_fifo_cost_net_money)}</td>
									<td>{drawCost(subItem?.total_fifo_cost_net_money)}</td>
									<td colSpan={item?.product_id || item?.linked_product?.id ? 4 : 3} />
								</tr>
							))}
					</React.Fragment>
				);
			})}
			<OrderItemsCardTableSummary taxItems={taxItems} order={finalOrder} />
			{productToLink && (
				<LinkEntityModal
					onHide={onHide}
					isShown={Boolean(productToLink)}
					key={productToLink.id}
					onSuccess={productToLink.id ? updateLinkedProductWithLink : updateLinkedProductWithoutLink}
					item={productToLink}
					channel={finalOrder?.channel}
				/>
			)}
		</tbody>
	);
};

export default OrderItemsCardTableBody;
